import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/shared/contexts/AuthContext'
import {
  getUnit,
  getCurrentUnit,
  setCurrentUnit as setCurrentUnitDB,
  getUserRoleInUnit,
} from '@/modules/units'
import type { Role } from '@/lib/permissions/types'
import type { Unit } from '@/modules/units/contexts/UnitContextTypes'

export function useCurrentUnitState() {
  const [currentUnit, setCurrentUnitState] = useState<Unit | null>(null)
  const [contextKey, setContextKey] = useState<string | null>(null)
  const [userRole, setUserRole] = useState<Role | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const { user, loading: authLoading } = useAuth()

  const clearCurrentUnit = useCallback(() => {
    setCurrentUnitState(null)
    setContextKey(null)
    setUserRole(null)
  }, [])

  const loadCurrentUnit = useCallback(async () => {
    const userId = user?.id

    if (!userId) {
      clearCurrentUnit()
      setIsLoading(false)
      return
    }

    setIsLoading(true)

    try {
      const unit = await getCurrentUnit(userId)

      if (!unit) {
        clearCurrentUnit()
        return
      }

      const enrichedUnit = await getUnit(unit.id)
      const resolvedRole = await getUserRoleInUnit(userId, unit.id)

      setCurrentUnitState(enrichedUnit as Unit)
      setUserRole(resolvedRole)
      setContextKey(enrichedUnit.context_key || null)
    } catch (error) {
      console.error('[useCurrentUnitState] Error loading current unit:', error)
      clearCurrentUnit()
    } finally {
      setIsLoading(false)
    }
  }, [user?.id, clearCurrentUnit])

  useEffect(() => {
    if (!authLoading) {
      void loadCurrentUnit()
    }
  }, [authLoading, loadCurrentUnit])

  useEffect(() => {
    const handleOnline = () => {
      void loadCurrentUnit()
    }

    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('online', handleOnline)
    }
  }, [loadCurrentUnit])

  const setCurrentUnit = useCallback(
    async (unitId: string) => {
      const userId = user?.id

      if (!userId) {
        console.warn('[useCurrentUnitState] Cannot set current unit: no userId')
        return
      }

      setIsLoading(true)

      try {
        await setCurrentUnitDB(userId, unitId)
        await loadCurrentUnit()
      } catch (error) {
        console.error('[useCurrentUnitState] Error setting current unit:', error)
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [user?.id, loadCurrentUnit],
  )

  const updateGlobalContext = useCallback((newContextKey: string) => {
    setContextKey(newContextKey)
  }, [])

  return {
    currentUnit,
    contextKey,
    userRole,
    isLoading,
    setCurrentUnit,
    refreshCurrentUnit: loadCurrentUnit,
    updateGlobalContext,
  }
}